/**
 * Scene-wide LOD floor (the "boot clamp" and the LOD reveal walk).
 *
 * Engine 2.20 moved `lodRangeMin` / `lodRangeMax` from `scene.gsplat` onto the
 * GSplatComponent, and in 2.22 the scene-level setters are silent no-ops (the
 * setter body is empty, the getter returns 0). viewer2's coarse-first boot —
 * clamp everything to the coarsest tier so the first paint needs only that
 * tier's bytes, then walk the clamp down behind the visible scene — needs ONE
 * value applied to every splat, including splats created after the value was
 * set. This module keeps that value per app and fans it out.
 */

import type * as pc from 'playcanvas';

const floors = new WeakMap<pc.AppBase, number>();

type LodComponent = { lodRangeMin: number } | null | undefined;

function splatComponents(app: pc.AppBase): LodComponent[] {
    return app.root.findComponents('gsplat') as unknown as LodComponent[];
}

/** Set the floor on every splat in the scene and remember it for later ones. */
export function setLodFloor(app: pc.AppBase, value: number): void {
    floors.set(app, value);
    for (const c of splatComponents(app)) {
        if (c) c.lodRangeMin = value;
    }
}

/** The floor last set for this app (0 = full detail allowed). */
export function getLodFloor(app: pc.AppBase): number {
    return floors.get(app) ?? 0;
}

/** Apply the remembered floor to a splat created after setLodFloor() ran. */
export function applyLodFloor(app: pc.AppBase, entity: pc.Entity): void {
    const v = floors.get(app);
    if (v !== undefined && entity.gsplat) entity.gsplat.lodRangeMin = v;
}
